import { AddressObject } from 'mailparser';
import { Types } from 'mongoose';

import { IBaseEntity } from '../common';

export enum MAILBOX_TYPES {
  INBOX = 'Inbox',
  SENT = 'Sent',
  DRAFT = 'Drafts',
  SPAM = 'Spam',
  TRASH = 'Trash',
  ARCHIVE = 'Archive',
  STARRED = 'starred',
}

export enum SEND_OPTIONS {
  CC = 'CC',
  BCC = 'BCC',
}

export enum EMAIL_VIEW_OPTIONS {
  FORWARD = 'Forward',
  REPLY = 'Reply',
  REPLY_ALL = 'Reply All',
  REPORT = 'REPORT SPAM',
  PERM_DELETE = 'DELETE_FOREVER',
}

export enum SELECT_TYPES {
  NONE = 'None',
  ALL = 'All',
  STARRED = 'Starred',
  UNSTARRED = 'Unstarred',
  UNREAD = 'Unread',
  READ = 'Read',
}

export enum TLS_TYPES {
  TLS1 = 'TLSv1.1',
  TLS2 = 'TLSv1.2',
  TLS3 = 'TLSv1.3',
}

export enum MAILBOX_CONFIG_TYPES {
  SMTP = 'SMTP',
  IMAP = 'IMAP',
}

// -DATE -> descending order
export enum SORT_CRITERIA {
  DATE = 'DATE', //Message sent date and time. Ascending
  ARRIVAL = 'ARRIVAL', //Internal date and time of the message.
  CC = 'CC', //The mailbox of the first "cc" address.
  FROM = 'FROM', //The mailbox of the first "from" address.
  SIZE = 'SIZE', // Size of the message in octets.
  SUBJECT = 'SUBJECT',
  TO = 'TO',
  DEC_ARRIVAL = '-ARRIVAL',
  DEC_CC = '-CC',
  DEC_DATE = '-DATE',
  DEC_FROM = '-FROM',
  DEC_SIZE = '-SIZE',
  DEC_SUBJECT = '-SUBJECT',
  DEC_TO = '-TO',
}

export enum MAILBOX_FLAGS {
  SEEN = '\\Seen',
  ANSWERED = '\\Answered',
  FLAGGED = '\\Flagged',
  DELETED = '\\Deleted',
  DRAFT = '\\Draft',
}

export enum MAILBOX_FLAG_ACTIONS {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
}

// the building of search criteria of this basic looks like
// ['ALL']
// [['OR', 'UNSEEN', ['SINCE', 'April 22, 2024']]]
// Note : By default, all criterion are ANDed together.
// You can use the special 'OR' on two criterion to find messages matching either search criteria.
export enum BASIC_MAILBOX_FILTERS {
  ALL = 'ALL',
  ANSWERED = 'ANSWERED',
  DELETED = 'DELETED',
  DRAFT = 'DRAFT',
  FLAGGED = 'FLAGGED',
  NEW = 'NEW',
  SEEN = 'SEEN',
  RECENT = 'RECENT',
  OLD = 'OLD',
  UNANSWERED = 'UNANSWERED',
  UNDELETED = 'UNDELETED',
  UNDRAFT = 'UNDRAFT',
  UNFLAGGED = 'UNFLAGGED',
  UNSEEN = 'UNSEEN',
  OR = 'OR',
}

// All of this requires a search term and should build the search criteria as
// ['FROM', 'example@echonlabs.com']
// ['SINCE', 'August 10, 2024']
// ['LARGER', '10000']
// [['HEADER', 'SUBJECT', 'node-imap']] the word node-imap that doesn't exists in header>subject
// [['!HEADER', 'SUBJECT', 'node-imap']] the word node-imap that exists in header>subject
export enum ADVANCE_MAILBOX_FILTERS {
  BCC = 'BCC',
  CC = 'CC',
  FROM = 'FROM',
  SUBJECT = 'SUBJECT',
  TO = 'TO',
  BODY = 'BODY',
  TEXT = 'TEXT',
  KEYWORD = 'KEYWORD',
  HEADER = 'HEADER',
  // DATE CRITERIA
  BEFORE = 'BEFORE',
  ON = 'ON',
  SINCE = 'SINCE',
  SENTBEFORE = 'SENTBEFORE',
  SENTON = 'SENTON',
  SENTSINCE = 'SENTSINCE',
  // SIZE WISE (Messages with a size larger than the specified number of bytes)
  LARGER = 'LARGER',
  SMALLER = 'SMALLER',
}

export interface IEmailClient extends IBaseEntity {
  isStarred: boolean;
}

export interface IDynamicSignFields {
  key?: string;
  value?: string;
}

export interface IMailboxSignature {
  richText: string;
  fields: IDynamicSignFields[]; // saves key in richtext and formControl name
  allowCustom?: boolean;
}

export interface IMailboxUserSignature {
  richText: string;
  fields: IDynamicSignFields[]; // saves formControl name and formValues
}

export interface ISmtpConfig {
  username?: string;
  password?: string;
  host: string;
  port: number;
  tls: string;
  secure?: boolean;
}

export interface IImapConfig {
  username?: string;
  password?: string;
  host: string;
  port: number;
  tls: string;
  secure?: boolean;
}

export interface IMailboxSetupConfig extends IBaseEntity {
  allowMultipleMailbox: boolean;
  sendTestMessages: boolean;
  smtpConfig: ISmtpConfig;
  imapConfig: IImapConfig;
  signature?: IMailboxSignature;
}

export interface IMailboxUserSettingsConfig extends IBaseEntity {
  displayName: string;
  imapConfig: IImapConfig;
  smtpConfig: ISmtpConfig;
  isCurrent: boolean;
  signature?: IMailboxUserSignature;
  isValidConnection?: boolean;
  created_by?: Types.ObjectId | string;
  isConfigured?: boolean;
}

export interface IAdvanceMailboxFilters {
  key: ADVANCE_MAILBOX_FILTERS;
  value: string | string[];
}

export interface ISearchCriteria {
  basic?: BASIC_MAILBOX_FILTERS | BASIC_MAILBOX_FILTERS[];
  advanced?: IAdvanceMailboxFilters[];
  or?: ISearchCriteria[];
  not?: ISearchCriteria;
}

export interface IMailboxEmailContact {
  name: string;
  email: string;
}

export interface IMailboxFlagEmail {
  uid: number[];
  email?: string;
  boxType: MAILBOX_TYPES;
  messageId?: string;
  action?: MAILBOX_FLAG_ACTIONS;
  flags?: MAILBOX_FLAGS;
  moveToBox?: MAILBOX_TYPES;
}

export interface IEmailAttachment {
  fileName: string;
  data: string;
  file: File;
  download_link?: string;
  filename?: string;
}

export interface IMailboxSendEmailOptions {
  from?: string;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  text?: string;
  html?: string;
  attachments?: IEmailAttachment[];
  from_name?: string;
  content_type?: string;
  messageId?: string;
}

export interface IMailboxReceiveEmail {
  uid: number;
  messageId?: string;
  flags: string[]; // this contains the UNSEEN / SEEN / STARRED / RECENT tags
  isStarred?: boolean;
  isSeen?: boolean;
  from: AddressObject | undefined;
  to: AddressObject | AddressObject[] | undefined | string[];
  cc?: AddressObject | AddressObject[] | undefined | string[];
  bcc?: AddressObject | AddressObject[] | undefined | string[];
  replyTo: AddressObject;
  subject?: string;
  date: Date | string;
  body?: string;
  bodyAsText: string;
  attachments: any[];
  email_id?: string;
  to_folder?: string;
  from_folder?: string;
}

export interface IMailboxEmailResponse {
  totalEmails: number;
  unreadEmails: number;
  mailbox: MAILBOX_TYPES;
  emails: IMailboxReceiveEmail[];
}

export interface IPushNotificationEmail {
  messageId?: string;
  from?: string;
  to?: string;
  subject?: string;
  date?: string;
  body?: string;
}

export enum SIDEBAR_ITEM_TYPE {
  COMPOSE = 'COMPOSE',
  INBOX = 'INBOX',
  SEND = 'SEND',
  DRAFT = 'DRAFT',
  SPAM = 'SPAM',
  EDIT = 'EDIT',
  MAIL = 'MAIL',
  ARCHIVE = 'ARCHIVE',
  TRASH = 'TRASH',
  SETTINGS = 'SETTINGS',
  PROFILE = 'PROFILE',
  STARRED = 'STARRED',
}

export interface ISideBarItems {
  btnType: SIDEBAR_ITEM_TYPE;
  label: string;
  icon?: string;
  is_active: boolean;
  count?: number;
}

export interface IUserListItem {
  name: string;
  email: string;
  is_active?: boolean;
}
